import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Youtube, Instagram, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { subscribeNewsletter } from "@/lib/api";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const year = new Date().getFullYear();

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            await subscribeNewsletter(email.trim());
            toast.success("You're on the list. Check your inbox on Monday.");
            setEmail("");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="bg-stone-950 text-stone-100 grain relative">
            <div className="container-editorial py-20 md:py-24 grid md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <p className="label-eyebrow text-stone-400">Newsletter</p>
                    <h3 className="font-serif text-3xl md:text-5xl leading-tight mt-3">
                        One curated email.
                        <br />
                        Every Monday.
                    </h3>
                    <p className="text-stone-400 mt-4 max-w-md">
                        Hand-tested product picks, honest reviews and buying tips —
                        delivered before your first coffee.
                    </p>
                    <form onSubmit={onSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md">
                        <Input
                            data-testid="footer-newsletter-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            className="bg-stone-900 border-stone-800 text-stone-100 placeholder:text-stone-500 h-11"
                        />
                        <Button
                            data-testid="footer-newsletter-submit"
                            type="submit"
                            disabled={loading}
                            className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-11 px-6"
                        >
                            {loading ? "Sending…" : "Subscribe"}
                        </Button>
                    </form>
                </div>

                <div className="md:col-span-2">
                    <p className="label-eyebrow text-stone-400">Explore</p>
                    <ul className="mt-4 space-y-2">
                        <li><Link className="hover:text-accent" to="/ai-tools" data-testid="footer-link-ai-tools">AI Tools</Link></li>
                        <li><Link className="hover:text-accent" to="/affiliate" data-testid="footer-link-affiliate">Picks</Link></li>
                        <li><Link className="hover:text-accent" to="/shorts" data-testid="footer-link-shorts">Shorts</Link></li>
                        <li><Link className="hover:text-accent" to="/ebooks" data-testid="footer-link-ebooks">eBooks</Link></li>
                        <li><a className="hover:text-accent text-accent" href="https://superprofile.bio/launchtoearn" target="_blank" rel="noopener noreferrer" data-testid="footer-link-shop">Shop ↗</a></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <p className="label-eyebrow text-stone-400">Company</p>
                    <ul className="mt-4 space-y-2">
                        <li><Link className="hover:text-accent" to="/about" data-testid="footer-link-about">About</Link></li>
                        <li><Link className="hover:text-accent" to="/blog" data-testid="footer-link-blog">Blog</Link></li>
                        <li><Link className="hover:text-accent" to="/faqs" data-testid="footer-link-faqs">FAQs</Link></li>
                        <li><Link className="hover:text-accent" to="/contact" data-testid="footer-link-contact">Contact</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <p className="label-eyebrow text-stone-400">Legal</p>
                    <ul className="mt-4 space-y-2">
                        <li><Link className="hover:text-accent" to="/privacy" data-testid="footer-link-privacy">Privacy Policy</Link></li>
                        <li><Link className="hover:text-accent" to="/affiliate-disclosure" data-testid="footer-link-disclosure">Referral Disclosure</Link></li>
                        <li><Link className="hover:text-accent" to="/disclaimer" data-testid="footer-link-disclaimer">Disclaimer</Link></li>
                        <li><Link className="hover:text-accent" to="/terms" data-testid="footer-link-terms">Terms & Conditions</Link></li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-stone-800">
                <div className="container-editorial py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="text-sm text-stone-500 max-w-3xl">
                        <p>© {year} {BRAND.name}. Built for the beginner builder.</p>
                        <p data-testid="footer-affiliate-disclosure" className="mt-2 text-stone-400 text-[13px] leading-relaxed">
                            Some links on this website are partner links. We may earn a small referral reward at no extra cost to you. Read our{" "}
                            <Link to="/affiliate-disclosure" className="underline hover:text-accent">Partners & Referral Disclosure</Link>.
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <a data-testid="social-youtube" href={BRAND.socials.youtube} target="_blank" rel="noreferrer" className="hover:text-accent"><Youtube className="h-5 w-5" /></a>
                        <a data-testid="social-instagram" href={BRAND.socials.instagram} target="_blank" rel="noreferrer" className="hover:text-accent"><Instagram className="h-5 w-5" /></a>
                        <a data-testid="footer-email" href={`mailto:${BRAND.email}`} className="text-sm hover:text-accent flex items-center gap-1">{BRAND.email}<ArrowUpRight className="h-3 w-3" /></a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
